import {
  ChangeEvent,
  FC,
  FocusEventHandler,
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { FieldValues, UseFormTrigger } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import moment from 'moment'
import { RestrictionDTO } from 'src/types/restriction'
import arrowRightIcon from '@assets/images/arrow-right.svg'
import {
  AutoCompleteInfiniteScroll,
  FormLabel,
  TextField,
} from '@microservices/wiskey-react-components'
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  InputProps,
  Paper,
  PaperProps,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material'

import { getColumnFieldNameForSort } from '@pages/ConfiguredEntity/helpers'

import { CustomSvgIcon } from '@components/CustomSvgIcon'

import {
  useFetchListControlOrDropdownObjectDataEnrichedMutation,
  useFetchObjectByCodeQuery,
  useFetchObjectDataRecordFormDropDownDefaultByFieldsMutation,
} from '@redux/api'

import { useDebounce, useInfiniteScroll, useKeyPress, useRestrictions } from '@hooks'
import {
  createRestrictions,
  generalFieldStylesByMode,
  getAutoCompletePopperSxByDisplayMethod,
  getFilterByValueType,
  getInitialPositionWindow,
  getLocalStorageContext,
  isNotEmpty,
  isObjectValueType,
  jsToRSQL,
} from '@helpers'
import {
  DROPDOWN_FILTER_TYPES,
  DROPDOWN_LIST_CLASSNAME,
  FIELD_VALUE_TYPE,
  FORM_CONTAINER_TAB_ID,
  FORM_TYPE,
  HOT_KEY,
  MIN_AUTOCOMPLETE_POPPER_WIDTH,
  OBJECT_FIELD_TYPE,
  SEARCH_PATTERN_DELAY,
  UPDATES_ROW_CLASSNAME,
} from '@constants'
import {
  AutocompleteOption,
  EntityType,
  FieldObjectParams,
  formFieldsValues,
  GETEnrichedFilteredEntityObjectDataParams,
  GETObjectDataParamsEnriched,
  LabelPlacement,
  NewObjectDataLinkType,
  ObjectDataRecord,
  ObjectFieldDTO,
} from '@types'
import { RESTRICTION_TYPE } from '@types/restriction'

import { EmbeddedObject } from '@gantt/components/GanttCreateOrEdit'

import { DropDownList } from '../DropDownList'
import { DropDownWindow } from '../DropDownWindow'

export type ObjectPickerProps = {
  valueType: FIELD_VALUE_TYPE
  generalFilterField: string
  trigger?: UseFormTrigger<FieldValues>
  isDropDownList?: boolean
  isDropDownWindow?: boolean
  dropDownList?: EntityType
  dropDownWindow?: EntityType
  value?: AutocompleteOption | null | string | AutocompleteOption[]
  dataValue?: string
  handleKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
  onChange?: (
    details: AutocompleteOption | AutocompleteOption[] | null,
    shouldDirty?: boolean | undefined
  ) => void
  invalid?: boolean
  label?: string
  labelPlacement?: LabelPlacement
  labelSx?: SxProps
  placeholder?: string
  name: string
  autoFocus?: boolean
  size?: 'small' | 'medium'
  objectCode?: string
  readOnly?: boolean
  objectValue?: string
  objectFormCode?: string
  isOptionEqualToValue?:
    | ((option: AutocompleteOption, value: AutocompleteOption) => boolean)
    | undefined
  objectFormTitle?: string
  getValue?: (value: NewObjectDataLinkType, event: MouseEvent<HTMLButtonElement>) => void
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  disableClearable?: boolean
  disabled?: boolean
  className?: string
  formatDate?: string
  onValidateInputValue?: (value: string) => boolean
  onChangeInput?: (value: string) => void
  objectFormCodeparams?: FieldObjectParams | string
  getFormCodeParams?: (value: FieldObjectParams, objectId: string, typeId: FIELD_VALUE_TYPE) => void
  onObjectNotFoundError?: (value: boolean) => void
  onChangeInDropdownEntityMode?: (
    value: AutocompleteOption | null,
    shouldDirty?: boolean | undefined
  ) => void
  inputProps?: InputProps
  multiple?: boolean
  type?: OBJECT_FIELD_TYPE
  isSearchAssistiant?: boolean
  onChangeMultipleValueDropdownEntity?: (
    value: AutocompleteOption[],
    shouldDirty?: boolean | undefined
  ) => void
  dirtyFields?: Partial<Readonly<{ [x: string]: boolean }>>
  formObjectId?: string
  formObjectCode?: string
  formElementId?: number
  valueId?: number
  fieldId?: number
  columnId?: number
  tabId?: number
  formType?: FORM_TYPE
  isViewEdit?: boolean
  popupIcon?: ReactNode
  allFieldsWithCurrentValues?: formFieldsValues
  sx?: SxProps<Theme>
  isDialogWindow?: boolean
  isDropdownWindowOpenFormState?: boolean
  handleSetDropdownWindowOpenFormState?: (value: boolean) => void
  parentDialogId?: string
  // Нужно для загрузки объекта, если в компонент передали лишь один id: string
  formId?: number
  pathArray?: EmbeddedObject[]
  fixAdornment?: boolean
}

const createObjectPickerOption = (
  item: GETObjectDataParamsEnriched,
  objLinkField: ObjectFieldDTO | undefined,
  objectValue: string
) => {
  return {
    ...item,
    id: objLinkField ? (item[objLinkField.name] as string | number) : '',
    label: isNotEmpty(item[objectValue]) ? `${item[objectValue]}` : '',
    internalId: item['internal_id'] || '',
  }
}

export const ObjectPicker: FC<ObjectPickerProps> = ({
  valueType,
  generalFilterField,
  trigger,
  isDropDownList = false,
  isDropDownWindow = false,
  dropDownList,
  allFieldsWithCurrentValues,
  dropDownWindow,
  isViewEdit,
  name,
  value,
  formType,
  formatDate,
  autoFocus = false,
  onChange,
  invalid,
  label,
  labelPlacement = 'start',
  labelSx,
  readOnly,
  placeholder,
  size = 'small',
  objectCode = '',
  objectValue = '',
  objectFormCode = '',
  isOptionEqualToValue = (value, option) => value.id === option.id,
  objectFormTitle = '',
  getValue,
  onBlur,
  disableClearable = true,
  disabled,
  className,
  onValidateInputValue,
  onChangeInput,
  getFormCodeParams,
  objectFormCodeparams,
  inputProps,
  onChangeInDropdownEntityMode,
  multiple,
  onChangeMultipleValueDropdownEntity,
  handleKeyDown,
  dirtyFields,
  formObjectId = '',
  formObjectCode,
  formElementId,
  valueId,
  fieldId,
  columnId,
  tabId,
  popupIcon,
  sx,
  isDialogWindow,
  handleSetDropdownWindowOpenFormState,
  parentDialogId,
  pathArray,
  fixAdornment,
  formId,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isObjectPicker = !isDropDownList && !isDropDownWindow
  const lastRefField = pathArray?.at(-1)
  const [shiftHeld, setShiftHeld] = useState(false)
  const [ctrlHeld, setCtrlHeld] = useState(false)
  const isInitialization = useRef(true)
  const inputRef = useRef<HTMLInputElement>(null)

  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setOpen] = useState(false)
  const [isDropDownWindowOpen, setDropDownWindowOpen] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const debouncedSearchValue = useDebounce(inputValue, SEARCH_PATTERN_DELAY)

  const [customInitialOffset, setCustomInitialOffset] = useState({ x: 0, y: 0 })

  const [localDropDownWindowValue, setLocalDropDownWindowValue] =
    useState<AutocompleteOption | null>(null)
  const [localMultipleDropDownWindowValue, setLocalMultipleDropDownWindowValue] = useState<
    AutocompleteOption[]
  >(value as AutocompleteOption[])

  const context = getLocalStorageContext()

  useEffect(() => {
    if (!multiple && !dropDownWindow) {
      return
    }

    setLocalMultipleDropDownWindowValue(value as AutocompleteOption[])
  }, [value])

  const {
    restrictions,
    updateRestrictions,
    removeRestrictions,
    getRestrictions,
    isLoadingRestrictions,
  } = useRestrictions(dropDownList?.id)
  const { data: objectModel, isFetching: isLoadingObjectModel } = useFetchObjectByCodeQuery(
    objectCode,
    {
      skip: !objectCode,
    }
  )

  const sortParams = useMemo(() => {
    let sort =
      restrictions?.sort?.map(sort => ({
        field: sort.value?.columnToSort,
        sort: sort.value?.sortDirection,
        priority: sort.value?.priority,
      })) || []

    sort = sort.sort((a, b) => Number(a.priority) - Number(b.priority))

    return isObjectPicker ? [{ field: objectValue, sort: 'asc' }] : sort
  }, [restrictions.sort])

  const columns = useMemo(() => {
    return dropDownList?.columns
      .map(column => {
        const order = restrictions.order?.find(rOrder => rOrder.fieldCode === column.code)
        // const width = restrictions.width?.find(rWidth => rWidth.fieldCode === column.code)
        const sort = restrictions.sort?.find(rSort => rSort.fieldCode === column.code)

        return {
          ...column,
          fieldCode: column.code,
          fieldName: column.title,
          sortKey: getColumnFieldNameForSort(column),
          order,
          // width,
          sort: {
            columnToSort: column.columnToSort,
            ...sort?.value,
          },
        }
      })
      .sort(({ order: orderA }, { order: orderB }) => orderA?.value - orderB?.value)
  }, [restrictions])

  const objLinkField = useMemo(
    () =>
      objectModel?.fields.find(field =>
        valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK ? field.isInternalId : field.isPk
      ),
    [objectModel?.fields, valueType]
  )

  const fieldValue = useMemo(
    () => objectModel?.fields.find(field => field.name === objectValue),
    [objectModel]
  )

  const isRefDateField = useMemo(
    () =>
      isObjectValueType(valueType) &&
      isArray(pathArray) &&
      pathArray[pathArray.length - 1].valueType === FIELD_VALUE_TYPE.DATETIME,
    [valueType, pathArray]
  )

  const isUpdated = className === UPDATES_ROW_CLASSNAME

  useEffect(() => {
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      setIsTyping(false)
      setInputValue('')

      return
    }

    if (!Array.isArray(value) && value && isObject(value)) {
      setInputValue(value.label ? (getFormattedOption(value.label.toString()) as string) : '')

      return
    }

    setInputValue(value)
  }, [value])

  const idForDropDownList = formElementId || valueId || fieldId

  // TODO разобраться с параметрами, какие-то возможно лишние
  const dropdownParamsForRequest: GETEnrichedFilteredEntityObjectDataParams & { skip?: boolean } = {
    formObjectCode,
    dropDownEntityType: DROPDOWN_FILTER_TYPES.LIST,
    viewId: dropDownList?.id,
    body: { formFields: allFieldsWithCurrentValues },
    skip: false,
    valueId,
    fieldId,
    columnId,
    formObjectId,
    contextId: context?.id,
  }

  const isSkip = useMemo(
    () => !isOpen || !objLinkField || !objectCode,
    [isOpen, objLinkField, objectCode]
  )

  const defaultParamsForRequest = {
    objectCode,
    fields: [objLinkField?.name, objectValue],
    skip: isSkip,
  }

  isDropDownList = Boolean(dropDownList && dropDownList.id)

  function downHandler({ key }) {
    if (key === 'Shift') {
      setShiftHeld(true)
    }
    if (key === 'Control') {
      setCtrlHeld(true)
    }
  }

  function upHandler({ key }) {
    if (key === 'Shift') {
      setShiftHeld(false)
    }
    if (key === 'Control') {
      setCtrlHeld(false)
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', downHandler)
    window.addEventListener('keyup', upHandler)

    if (autoFocus) {
      handleDropDownOpen()
    }

    return () => {
      window.removeEventListener('keydown', downHandler)
      window.removeEventListener('keyup', upHandler)
    }
  }, [])

  const handleUpdateRestrictions = async (restriction: RestrictionDTO) => {
    if (!dropDownList?.id) return
    let newRestriction = { ...restriction }
    newRestriction.viewId = dropDownList.id
    if (!shiftHeld && !ctrlHeld) {
      newRestriction.value.priority = 0
      await removeRestrictions(RESTRICTION_TYPE.SORT)
      if (!newRestriction.value?.sortDirection) {
        return
      }
    } else {
      if (newRestriction.value?.sortDirection) {
        newRestriction.value.priority = newRestriction.value.priority ?? restrictions.sort?.length
      } else {
        newRestriction.value = undefined
        await updateRestrictions([
          ...restrictions.sort
            .filter(restr => restr.value?.priority > (restriction.value?.priority || 0))
            .map(restr => ({
              ...restr,
              viewId: dropDownList.id,
              restrictionType: RESTRICTION_TYPE.SORT,
              value: `${restr.value?.columnToSort}//${restr.value?.sortDirection}//${
                restr.value?.priority - 1
              }`,
            })),
          newRestriction,
        ])

        return
      }
    }
    newRestriction = newRestriction.value
      ? createRestrictions({
          ...newRestriction,
          ...newRestriction.value,
        })
      : newRestriction
    await updateRestrictions([newRestriction])
  }

  const entityRequestParams = isDropDownList ? dropdownParamsForRequest : defaultParamsForRequest

  const useEntityObjectDataMutation = isDropDownList
    ? useFetchListControlOrDropdownObjectDataEnrichedMutation
    : useFetchObjectDataRecordFormDropDownDefaultByFieldsMutation

  useEffect(() => {
    document
      .getElementById(`${FORM_CONTAINER_TAB_ID}_${tabId}`)
      ?.addEventListener('scroll', handleDropDownClose)

    return () => {
      document
        .getElementById(`${FORM_CONTAINER_TAB_ID}_${tabId}`)
        ?.removeEventListener('scroll', handleDropDownClose)
    }
  }, [])

  const {
    combinedData,
    readMore,
    refresh,
    onSetParams,
    currentPage,
    totalPages,
    isLoading: isLoadingData,
  } = useInfiniteScroll<ObjectDataRecord, GETObjectDataParamsEnriched>(
    useEntityObjectDataMutation,
    entityRequestParams,
    undefined,
    { sort: sortParams, filter: jsToRSQL(generalFilterField) },
    undefined,
    false
  )

  const getFormattedOption: (
    option: string | AutocompleteOption | AutocompleteOption[]
  ) => string | AutocompleteOption | AutocompleteOption[] = option => {
    if (Array.isArray(option)) {
      return option.map(getFormattedOption) as AutocompleteOption[]
    }

    if (isObject(option)) {
      return {
        ...option,
        label: getFormattedOption(option.label),
      } as AutocompleteOption
    }

    if (Boolean(formatDate) && moment(option).isValid() && isRefDateField) {
      return moment(option).format(formatDate).toString()
    }

    return option
  }

  const handleResetFilter = () => {
    onSetParams({
      page: 0,
      filter: jsToRSQL(generalFilterField),
      sort: sortParams,
    })
  }

  const dependenciesForInputFilterField = [
    debouncedSearchValue,
    fieldValue?.valueType,
    isRefDateField,
    objLinkField,
    objectValue,
    value?.label,
    isTyping,
    lastRefField,
  ]

  const inputFilterField = useMemo(() => {
    return debouncedSearchValue && typeof debouncedSearchValue !== 'object'
      ? getFilterByValueType(
          objectValue,
          isTyping,
          debouncedSearchValue,
          value,
          fieldValue?.valueType || FIELD_VALUE_TYPE.STRING,
          objLinkField,
          isRefDateField,
          lastRefField
        )
      : undefined
  }, dependenciesForInputFilterField)

  const [fetchDataForDDL] = useFetchListControlOrDropdownObjectDataEnrichedMutation()

  useEffect(() => {
    const shouldExtendInitialDataDDL =
      isDropDownList && value && objLinkField && objectValue && isInitialization.current && formId

    if (!shouldExtendInitialDataDDL) return

    isInitialization.current = false

    const filterForCertainValue = `${objLinkField.name}=in=('${value.id}')`

    const fetchAndExtendDataOption = async () => {
      try {
        const result = await fetchDataForDDL({
          ...dropdownParamsForRequest,
          filter: filterForCertainValue,
        }).unwrap()

        const extendedValue = result.data[0]

        const option = createObjectPickerOption(extendedValue, objLinkField, objectValue)

        onChange?.(option, false)
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error)
      }
    }

    fetchAndExtendDataOption()
  }, [objLinkField])

  useEffect(() => {
    if (isSkip) {
      return
    }

    const combinedFilter = [inputFilterField, jsToRSQL(generalFilterField)]
      .filter(Boolean)
      .join(';')

    onSetParams({
      page: 0,
      filter: combinedFilter ? combinedFilter : undefined,
      sort: sortParams,
    })
  }, [inputFilterField, generalFilterField, sortParams, isTyping, isSkip])

  const options: AutocompleteOption[] = useMemo(() => {
    return (
      combinedData?.map(item => createObjectPickerOption(item, objLinkField, objectValue)) ?? []
    )
  }, [combinedData, objLinkField, objectValue])

  const loadNextPage = () => {
    if (totalPages !== currentPage && !isLoadingData) {
      readMore()
    }
  }

  // TODO Похоже нужно избавиться, так как значение теперь должно быть типа AutocompleteOption
  // const isLoading = value && isString(value) && !isCreateType

  const renderPaperComponent = useCallback((props: PaperProps) => {
    return (
      <Paper
        {...props}
        sx={{
          minWidth: MIN_AUTOCOMPLETE_POPPER_WIDTH,
          overflowX: 'scroll',
        }}
      />
    )
  }, [])

  const handleDropDownOpen = async (event?: SyntheticEvent<Element, Event>) => {
    const eventTarget = event?.target as HTMLElement

    await getRestrictions()

    if (readOnly && (eventTarget.tagName === 'INPUT' || eventTarget.tagName === 'DIV')) {
      return
    }
    setIsTyping(false)
    setOpen(true)
  }

  const handleDropDownClose = () => {
    setOpen(false)
    setIsTyping(false)
  }

  function objectToOptions(obj: Record<string, string>): AutocompleteOption[] {
    if (!obj || typeof obj !== 'object') return []

    return Object.entries(obj).map(([id, label]) => ({ id, label }))
  }

  const handleChangeAutocomplete = (details: AutocompleteOption | null | AutocompleteOption[]) => {
    if (readOnly) {
      return
    }

    if (!details) {
      setInputValue('')
      setIsTyping(false)
      onChange?.(multiple ? [] : null)

      return
    }

    onChange?.(details)
    // делать сброc, только когда был поиск по значению
    if (inputValue && multiple) {
      refresh({ filter: undefined })
    }

    if (inputRef.current && multiple) {
      const inputRoot = inputRef.current.closest?.('.MuiInputBase-root') as HTMLElement | null
      if (inputRoot) {
        inputRoot.scrollLeft = inputRoot.scrollWidth
      }
    }

    setIsTyping(false)
    setInputValue('')
  }

  const renderInput = () => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (readOnly) {
        return
      }

      const value = e.target.value

      if (!onValidateInputValue?.(value)) {
        return
      }

      onChangeInput?.(value)
      if (value === inputValue) {
        return
      }

      setIsTyping(true)

      setInputValue(value)
    }

    const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = e => {
      onBlur?.(e)
      setIsTyping(false)

      if (
        (typeof value === 'object' && value?.label === inputValue) ||
        ((typeof value === 'number' || typeof value === 'string') && value === inputValue)
      ) {
        return
      }

      if (typeof value === 'object' && value?.label !== inputValue) {
        setInputValue(value?.label)

        return
      }

      if ((typeof value === 'string' || typeof value === 'number') && value !== inputValue) {
        setInputValue(value)
      }

      handleResetFilter()
      setInputValue('')
    }

    const handleClearIcon = () => {
      handleResetFilter()
      setInputValue('')
      onChange?.(multiple ? [] : null)
      setIsTyping(false)
      // trigger?.(name) // to do Временно выключил проверку на валидацию после очистки филда
    }

    const isLinkedObject =
      objectFormCode && value && isObject(value) && !Array.isArray(value) && value.id

    const handleOpenForm = (event: MouseEvent<HTMLButtonElement>) => {
      if (!Array.isArray(value)) {
        if (isLinkedObject) {
          getValue?.(
            {
              objectId: String(value.id), // to do Было изменено в следствиях бага WF-5454. Раньше подставлялось значение formObjectId
              objectCode,
              formCode: objectFormCode,
              title: objectFormTitle,
              isShiftKey: event.shiftKey,
              typeId: valueType,
            },
            event
          )

          return
        }
        if (objectFormCodeparams && isObject(objectFormCodeparams) && value && isObject(value)) {
          getFormCodeParams?.(objectFormCodeparams, `${value.id}`, valueType)
        }
      }
    }

    const handleDoubleClick: MouseEventHandler<HTMLDivElement> = e => {
      // setCustomInitialOffset({ x: 300, y: -(document.documentElement.scrollHeight - e.pageY) })
      e.stopPropagation()
      const initialPosition = getInitialPositionWindow(e)
      setCustomInitialOffset(initialPosition)
      setOpen(false)

      if (isDropDownWindow) {
        setDropDownWindowOpen(true)
        handleSetDropdownWindowOpenFormState?.(true)
      }
    }

    const isLoadingDropDown = isLoadingData || isLoadingObjectModel
    const verifiedInputValue =
      typeof inputValue === 'object' ? inputValue?.label ?? '' : String(inputValue ?? '')
    // Нельзя допускать undefined в value, иначе AutoComplete станет uncontrolled
    // Чинит https://jiradc.utg.group/browse/WF-3446
    // https://github.com/mui/material-ui/issues/36467
    const prepareValue = (value?: AutocompleteOption | null | string | AutocompleteOption[]) => {
      if (multiple) {
        return value ? getFormattedOption(value) : []
      }

      if (typeof value === 'string' || typeof value === 'number') {
        return { id: idForDropDownList, label: value }
      }

      return value ? getFormattedOption(value) : null
    }

    const getPaddingRightForInput = () => {
      const hasLinkAndClearButton = isLinkedObject || objectFormCodeparams
      const hasClearButton = !disableClearable

      if (hasLinkAndClearButton && hasClearButton) {
        return '65px !important'
      }

      if (hasLinkAndClearButton || hasClearButton) {
        return '45px !important'
      }

      return '20px !important'
    }

    return (
      <AutoCompleteInfiniteScroll
        fullWidth
        PaperComponent={renderPaperComponent}
        className={className}
        disableClearable={value ? disableClearable : true}
        disableCloseOnSelect={multiple}
        disabled={disabled && !readOnly}
        getOptionLabel={option => (isObject(option) ? option.label : '')}
        inputValue={verifiedInputValue}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={isLoadingDropDown}
        multiple={multiple}
        open={isOpen}
        options={options}
        page={currentPage}
        popupIcon={popupIcon}
        size={size}
        sx={sx}
        totalPages={totalPages}
        value={prepareValue(value)}
        componentsProps={{
          clearIndicator: {
            onClick: handleClearIcon,
          },
          popper: {
            className: isDropDownList ? DROPDOWN_LIST_CLASSNAME : undefined,
            sx: {
              zIndex: theme => theme.zIndex.tooltip,
              ...getAutoCompletePopperSxByDisplayMethod(isDialogWindow, readOnly, theme),
              border: isDropDownList ? `1px solid #303030` : 'none',
              borderTop: 'none',
              borderBottom: 'none',
              boxSizing: 'content-box',
            },
          },
        }}
        renderInput={params => {
          return (
            <>
              <TextField
                {...params}
                autoFocus={autoFocus}
                error={invalid}
                inputRef={inputRef}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(isLinkedObject || objectFormCodeparams) && (
                        <InputAdornment
                          position='end'
                          sx={{
                            position: 'absolute',
                            right: 20,
                            bottom: isDialogWindow ? 12 : 20,
                            zIndex: theme => theme.zIndex.modal,
                          }}
                        >
                          <IconButton
                            sx={{ mr: '7px', padding: isDialogWindow ? 0 : 1 }}
                            title={
                              objectFormTitle
                                ? t('objectPicker.openForm', { formTitle: objectFormTitle })
                                : undefined
                            }
                            onClick={handleOpenForm}
                          >
                            <CustomSvgIcon
                              fontSize={isDialogWindow ? 'small' : 'medium'}
                              src={arrowRightIcon}
                            />
                          </IconButton>
                        </InputAdornment>
                      )}
                      {!fixAdornment && params.InputProps.endAdornment}
                    </>
                  ),
                  startAdornment: (
                    <>
                      {inputProps?.startAdornment}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  ...inputProps?.inputProps,
                  onKeyDown: multiple && isOpen ? null : handleKeyDown,
                }}
                sx={theme => ({
                  ...generalFieldStylesByMode(theme, dirtyFields, name, isUpdated, isDialogWindow),
                  '.MuiInputBase-root': {
                    minHeight: '23px',
                    maxHeight: '100%',
                    pr: getPaddingRightForInput(),
                    '& .MuiChip-root': {
                      height: '19px',
                    },
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '0px !important',
                  },
                  '.MuiOutlinedInput-root': {
                    backgroundColor: readOnly ? theme.palette.background.hovered : undefined,
                  },
                  '.MuiAutocomplete-clearIndicator': {
                    marginRight: isLinkedObject || objectFormCodeparams ? '24px' : 'unset',
                  },
                })}
                onBlur={handleBlur}
                onChange={handleChange}
                onDoubleClick={handleDoubleClick}
              />
              {fixAdornment && params.InputProps.endAdornment && (
                <Box
                  sx={{
                    pointerEvents: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {params.InputProps.endAdornment}
                </Box>
              )}
            </>
          )
        }}
        renderOption={(props, option) => {
          if (isDropDownWindowOpen) return

          return isDropDownList ? (
            <DropDownList
              columns={columns}
              combinedData={combinedData}
              dropDownList={dropDownList}
              isLoading={isLoadingRestrictions}
              option={option}
              pkFieldName={objLinkField?.name}
              props={props}
              showSortOrder={restrictions.sort?.length > 1}
              onUpdateRestrictions={handleUpdateRestrictions}
            />
          ) : (
            <li
              {...props}
              key={option.id}
              style={{ paddingLeft: 0, paddingRight: 0 }}
              title={option.label}
            >
              <Box
                sx={{
                  overflowX: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  paddingLeft: 2,
                  width: '100%',
                }}
              >
                {getFormattedOption(option.label) as string}
              </Box>
            </li>
          )
        }}
        onChange={handleChangeAutocomplete}
        onClose={handleDropDownClose}
        onLoadNextPage={loadNextPage}
        onOpen={handleDropDownOpen}
      />
    )
  }

  const drowdownWindowValue = multiple
    ? { multipleSelectPickerOption: localMultipleDropDownWindowValue }
    : { selectedPickerOptionValue: value ? value : '' }

  const handleLocalDropDownValueChange = (row: ObjectDataRecord) => {
    const option = createObjectPickerOption(row, objLinkField, objectValue)
    setLocalDropDownWindowValue(option)
  }

  const handleLocalMultipleDropDownValueChange = (rows: ObjectDataRecord[]) => {
    const options = rows.map(row => createObjectPickerOption(row, objLinkField, objectValue))
    setLocalMultipleDropDownWindowValue(options)
  }

  const handleDropDownWindowClose = ({
    shouldApplyValueToForm,
  }: {
    shouldApplyValueToForm: boolean
  }) => {
    if (localDropDownWindowValue && shouldApplyValueToForm) {
      onChangeInDropdownEntityMode?.(localDropDownWindowValue)
    }

    if (multiple && localMultipleDropDownWindowValue && shouldApplyValueToForm) {
      onChangeMultipleValueDropdownEntity?.(localMultipleDropDownWindowValue)
    }

    if (localMultipleDropDownWindowValue && !shouldApplyValueToForm) {
      setLocalMultipleDropDownWindowValue(value as AutocompleteOption[])
    }

    handleDropDownClose()
    setDropDownWindowOpen(false)
    setTimeout(() => {
      handleSetDropdownWindowOpenFormState?.(false)
    }, 0)
  }

  const { keyPressed: exitDropdownWindowTrigger, setKeyPressed: setExitDropdownTriggerKeyPressed } =
    useKeyPress(HOT_KEY.ESCAPE, undefined, !isDropDownWindowOpen)

  const { keyPressed: saveDropdownWindowValueTrigger } = useKeyPress(
    HOT_KEY.ENTER,
    undefined,
    !isDropDownWindowOpen
  )

  useEffect(() => {
    if (saveDropdownWindowValueTrigger) {
      handleDropDownWindowClose({ shouldApplyValueToForm: true })

      return
    }

    if (exitDropdownWindowTrigger && !saveDropdownWindowValueTrigger) {
      handleDropDownWindowClose({ shouldApplyValueToForm: false })
      setExitDropdownTriggerKeyPressed(false)

      return
    }
  }, [saveDropdownWindowValueTrigger, exitDropdownWindowTrigger])

  return (
    <>
      {isDropDownWindowOpen &&
        dropDownWindow &&
        createPortal(
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              // display: 'block',
              // bottom: 0,
              // right: 0,
              // zIndex: theme => theme.zIndex.modal,
            }}
          >
            <DropDownWindow
              columnId={columnId}
              customInitialOffset={customInitialOffset}
              dropDownWindow={dropDownWindow}
              elementId={formElementId}
              fieldId={fieldId}
              formObjectCode={formObjectCode}
              formObjectId={formObjectId}
              generalFilterField={generalFilterField}
              isDialogWindow={isDialogWindow}
              objectValueName={objectValue}
              parentDialogId={parentDialogId}
              readonly={readOnly}
              theme={theme}
              onMultiSelectPickerOption={
                multiple ? handleLocalMultipleDropDownValueChange : undefined
              }
              // onSelectPickerOption={onChangeInDropdownEntityMode}
              // onMultiSelectPickerOption={onChangeMultipleValueDropdownEntity}
              valueId={valueId}
              onClose={handleDropDownWindowClose}
              onSelectPickerOption={handleLocalDropDownValueChange}
              {...drowdownWindowValue}
            />
          </Box>,
          document.body
        )}
      <Grid container item justifyContent={'space-between'}>
        <FormLabel
          control={renderInput()}
          label={label}
          labelPlacement={labelPlacement}
          labelSx={labelSx}
          name={name}
        />
      </Grid>
    </>
  )
}
